export const ETAPAS = [
  'Novo',
  'Em contato',
  'Diagnóstico',
  'Proposta',
  'Pedido',
] as const;

export const ETAPA_GROUPS = {
  novos: [0],
  topo: [1],
  meio: [2, 3],
  fim: [4],
} as const;

// Legacy etapa names → new names (for DB migration)
export const ETAPA_MIGRATION: Record<string, string> = {
  'Oportunidade Identificada': 'Diagnóstico',
  'Necessidade Identificada': 'Diagnóstico',
  'Diagnóstico em Andamento': 'Diagnóstico',
  'Novo Lead': 'Novo',
  'Sem Etapa': 'Novo',
  'Prospecção': 'Em contato',
  'Retorno': 'Em contato',
  'Frio': 'Em contato',
  'Qualificação': 'Diagnóstico',
  'Negociação': 'Proposta',
  'Cliente': 'Pedido',
};

/**
 * Auto-progressão: sugere próxima etapa com base na ação registrada.
 * Retorna null se não deve avançar.
 */
export function getNextEtapa(
  currentEtapa: string,
  activityType: string,
): string | null {
  const tipo = activityType.toLowerCase();
  const isVisita = tipo.includes('visita') || tipo.includes('check-in') || tipo.includes('checkin') || tipo === 'presencial';
  const isDiagnostico = tipo.includes('diagnóstico') || tipo.includes('diagnostico') || tipo.includes('análise') || tipo.includes('analise');
  const isProposta = tipo.includes('proposta') || tipo.includes('simulação') || tipo.includes('simulacao') || tipo.includes('orçamento') || tipo.includes('orcamento');

  switch (currentEtapa) {
    case 'Novo':
      // Qualquer contato → Em contato
      return 'Em contato';
    case 'Em contato':
      // Visita/diagnóstico → Diagnóstico
      if (isVisita || isDiagnostico) return 'Diagnóstico';
      return null;
    case 'Diagnóstico':
      // Proposta/simulação → Proposta
      if (isProposta) return 'Proposta';
      return null;
    default:
      return null;
  }
}

export const STATUS_LIST = [
  'Não Contatado',
  'Tentando Contato',
  'Em Conversa',
  'Aguardando Retorno',
  'Não Respondeu',
  'Em Nutrição',
  'Incubação',
  'Sem Perfil',
  'Sem Interesse',
  'Perdido',
  'Perda de Contato',
  'Ganho',
] as const;

export const INACTIVE_STATUSES = ['Perdido', 'Sem Interesse', 'Sem Perfil', 'Perda de Contato'];

/** Leads estacionados — não aparecem no kanban mas ficam no rodapé do funil */
export const PARKED_STATUSES = ['Incubação', 'Sem Perfil', 'Perda de Contato'];

export const TEMP_COLORS: Record<number, string> = {
  0: '#94a3b8',
  1: '#7cacdf',
  2: '#6b9fd4',
  3: '#d4a853',
  4: '#c99a45',
  5: '#c47a3a',
  6: '#b85c5c',
  7: '#a84e4e',
  8: '#9576b5',
};

export const TEMP_LABELS: Record<number, string> = {
  0: 'Sem temp',
  1: 'Muito frio',
  2: 'Frio',
  3: 'Morno',
  4: 'Morno+',
  5: 'Quente',
  6: 'Muito quente',
  7: 'Fervendo',
  8: 'Urgente',
};

// When a status is selected, suggest moving to this etapa
// Helps consultants who don't understand the funnel — they pick what happened, system guides where the lead goes
export const STATUS_TO_ETAPA_SUGGESTION: Record<string, string> = {
  'Não Contatado': 'Novo',
  'Tentando Contato': 'Em contato',
  'Não Respondeu': 'Em contato',
  'Em Conversa': 'Em contato',
  'Aguardando Retorno': 'Diagnóstico',
  'Em Nutrição': 'Diagnóstico',
  'Ganho': 'Pedido',
};

// Campos comuns a ambas as verticais
export const DIAG_FIELDS_COMMON = [
  { key: 'diag_potencial', label: 'Potencial' },
  { key: 'diag_perfil', label: 'Perfil' },
  { key: 'diag_cidade', label: 'Cidade' },
  { key: 'diag_fornecedor', label: 'Fornecedor' },
  { key: 'diag_decisor', label: 'Decisor' },
  { key: 'diag_dor', label: 'Dor' },
] as const;

// Campos exclusivos Agro
export const DIAG_FIELDS_AGRO = [
  { key: 'diag_fazenda', label: 'Fazenda' },
  { key: 'diag_volume', label: 'Volume' },
  { key: 'diag_area', label: 'Área' },
  { key: 'diag_cultura', label: 'Cultura' },
] as const;

// Campos exclusivos Água
export const DIAG_FIELDS_AGUA = [
  { key: 'diag_local_instalacao', label: 'Local / Planta', type: 'text' },
  { key: 'diag_fonte_agua', label: 'Fonte de água', type: 'select', options: ['Poço Artesiano', 'Rio', 'Represa', 'Rede Tratada', 'Nascente'] },
  { key: 'diag_segmento_agua', label: 'Segmento', type: 'select', options: ['Avicultura Industrial', 'Confinamento Bovino', 'Frigorífico', 'Indústria de Alimentos', 'Laticínios', 'Hotelaria', 'ETA Rural', 'Outro'] },
  { key: 'diag_vazao', label: 'Vazão (m³/h)', type: 'text' },
  { key: 'diag_problema_agua', label: 'Problema identificado', type: 'multiselect', options: ['Dureza elevada', 'Ferro/Manganês', 'Turbidez', 'pH fora da faixa', 'Sólidos dissolvidos', 'Contaminação microbiológica', 'Incrustação/Corrosão'] },
  { key: 'diag_solucao_interesse', label: 'Solução de interesse', type: 'multiselect', options: ['Filtro', 'Abrandador', 'Dosador', 'UV', 'ETA', 'ETE', 'TEFEN'] },
  { key: 'diag_modelo_negocio', label: 'Modelo preferido', type: 'select', options: ['Venda de Equipamento', 'Locação', 'Serviço + Equipamento'] },
  { key: 'diag_tratamento_atual', label: 'Tratamento atual', type: 'text' },
] as const;

/** @deprecated Use getDiagFields(vertical) instead */
export const DIAG_FIELDS = [
  ...DIAG_FIELDS_COMMON,
  ...DIAG_FIELDS_AGRO,
] as const;

/** Returns the right diagnostic fields based on the consultant vertical */
export function getDiagFields(vertical: string | null | undefined) {
  const v = (vertical ?? '').toUpperCase().replace('Á', 'A');
  const isAgua = v === 'AGUA';
  return [
    ...DIAG_FIELDS_COMMON,
    ...(isAgua ? DIAG_FIELDS_AGUA : DIAG_FIELDS_AGRO),
  ];
}

// SLA por etapa (dias máximos recomendados)
export const ETAPA_SLA: Record<string, number> = {
  'Novo': 2,
  'Em contato': 5,
  'Diagnóstico': 10,
  'Proposta': 10,
  'Pedido': 5,
};

// Motivos de perda (obrigatório ao marcar Perdido/Sem Interesse/Sem Perfil/Incubação)
// Closed Lost (consultor): tinha PERFIL, virou oportunidade real e não fechou.
export const MOTIVOS_PERDA = [
  'Preço',
  'Fechou com concorrente',
  'Sem decisão / status quo',
  'Timing (adiou)',
  'Sem orçamento',
  'Sumiu / não respondeu',
  'Outro',
] as const;

// Desqualificação (pré-venda/consultor): NÃO-PERFIL, antes de virar oportunidade.
// Não conta como venda perdida (fica fora do win rate).
export const MOTIVOS_DESQUALIFICACAO = [
  'Fora do perfil',
  'Sem necessidade',
  'Sem orçamento / porte',
  'Não é decisor',
  'Região inviável',
  'Contato inválido / curioso',
  'Fit-futuro (sem timing)',
  'Outro',
] as const;

// Perda de contato — cliente parou de responder (NÃO é desqualificação por perfil).
export const MOTIVOS_PERDA_CONTATO = [
  'Não atende / não retorna',
  'Sumiu após 1º contato',
  'Sumiu após interesse',
  'Sumiu após proposta',
  'Número inválido',
  'Outro',
] as const;

// Motivos que NÃO encerram de vez: voltam pra nutrição numa data de reativação.
export const MOTIVOS_REATIVAVEIS: string[] = ['Timing (adiou)', 'Fit-futuro (sem timing)', 'Sumiu após interesse', 'Sumiu após proposta'];

// Status de saída que são DESqualificação (não-perfil), não perda de venda.
export const STATUS_DESQUALIFICACAO = ['Sem Perfil', 'Sem Interesse', 'Incubação'];

// Cadência sugerida por etapa (dias até próximo contato)
export const CADENCIA_SUGERIDA: Record<string, { dias: number; acao: string }> = {
  'Novo Lead': { dias: 1, acao: 'Ligar' },
  'Prospecção': { dias: 3, acao: 'Ligar / WhatsApp' },
  'Qualificação': { dias: 5, acao: 'Agendar visita' },
  'Diagnóstico': { dias: 7, acao: 'Follow-up diagnóstico' },
  'Proposta': { dias: 5, acao: 'Follow-up proposta' },
  'Negociação': { dias: 3, acao: 'Follow-up negociação' },
  'Pedido': { dias: 2, acao: 'Confirmar pedido' },
};

export const HISTORICO_TIPOS = [
  'Visita',
  'Ligação',
  'WhatsApp',
  'E-mail',
  'Reunião',
  'Pedido',
  'Reclamação',
  'Outro',
] as const;

// ── Inteligência do Cliente ──

export const INTEL_SEGMENTOS = [
  'Agricultura',
  'Pecuária',
  'Indústria',
  'Compostagem',
  'Água',
] as const;

export const INTEL_PORTES = ['Pequeno', 'Médio', 'Grande'] as const;

export const INTEL_PREF_COMUNICACAO = [
  'WhatsApp',
  'Telefone',
  'Email',
  'Presencial',
] as const;

export const INTEL_HORARIOS_CONTATO = [
  'Manhã (8h-12h)',
  'Almoço (12h-14h)',
  'Tarde (14h-18h)',
  'Noite (18h-21h)',
  'Qualquer horário',
] as const;

export const INTEL_TIPOS_SOLO = [
  'Arenoso',
  'Argiloso',
  'Siltoso',
  'Humífero',
  'Calcário',
  'Latossolo',
  'Outro',
] as const;

export const INTEL_IRRIGACAO_TIPOS = [
  'Pivô Central',
  'Gotejamento',
  'Aspersão',
  'Sulcos',
  'Microaspersão',
  'Outro',
] as const;

export const INTEL_FONTES_AGUA = [
  'Poço',
  'Rio',
  'Represa',
  'Tratada',
  'Nascente',
  'Outro',
] as const;

export const INTEL_NOTA_TIPOS = [
  'Visita',
  'Ligação',
  'Observação',
  'Pesquisa',
] as const;

export const CULTURAS = [
  'Soja', 'Milho', 'Café', 'Cana', 'Algodão', 'Feijão', 'Sorgo', 'Pastagem', 'Outro',
] as const;
